import { useState, useRef, ChangeEvent, useImperativeHandle, forwardRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Paperclip, 
  X, 
  Image as ImageIcon, 
  File as FileIcon,

  Loader2,
  AlertCircle
} from 'lucide-react'
import { cn } from '@/lib/utils'
import chatFileUploadService from '@/services/chatFileUploadService'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface ChatFileUploadProps {
  conversationId: string
  onFileMessageSent: (message: any) => void
  onError?: (error: string) => void
  className?: string
  disabled?: boolean
  // New props to integrate with main chat input
  onFileSelected?: (file: File | null) => void
  onMessageChange?: (message: string) => void
  message?: string
  hideActionButtons?: boolean
}

export interface ChatFileUploadRef {
  handleSend: () => Promise<void>
  canSend: boolean
  isUploading: boolean
  selectedFile: File | null
  clearFile: () => void
  toggleFileInput: () => void
  selectFile: () => void
}

const ChatFileUpload = forwardRef<ChatFileUploadRef, ChatFileUploadProps>(({
  conversationId,
  onFileMessageSent,
  onError,
  className,
  disabled = false,
  onFileSelected,
  onMessageChange,
  message: externalMessage,
  hideActionButtons = false
}, ref) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [internalMessage, setInternalMessage] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState('')
  const [showFileInput, setShowFileInput] = useState(false)
  const [fileInputKey, setFileInputKey] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Use external message if provided, otherwise use internal
  const message = externalMessage !== undefined ? externalMessage : internalMessage
  const setMessage = onMessageChange || setInternalMessage

  // Reset file input when conversation changes
  useEffect(() => {
    resetFileInput()
    setSelectedFile(null)
    setError('')
  }, [conversationId])

  // Handle file selection
  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    // Check if file type is supported
    if (!chatFileUploadService.isSupportedFileType(file)) {
      setError('File type not supported. Please select an image, PDF, or document file.')
      return
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.')
      return
    }

    setSelectedFile(file)
    setError('')
    setShowFileInput(false)
    
    // Notify parent about file selection
    if (onFileSelected) {
      onFileSelected(file)
    }
  }

  // Handle file upload and message send
  const handleSend = async () => {
    if (!selectedFile) return
    if (!conversationId) return

    try {
      setIsUploading(true)
      setError('')

      // Upload file with optional message (empty string if no message)
      const response = await chatFileUploadService.uploadFileMessage(
        selectedFile,
        conversationId,
        message.trim() || '' // Send empty string if no message
      )

      // Notify parent component
      onFileMessageSent(response)

      // Reset form
      setSelectedFile(null)
      setMessage('')
      resetFileInput()
      // Also reset the file input key to force recreation
      setFileInputKey(prev => prev + 1)
    } catch (err: any) {
      // console.error('Error sending file message:', err)
      const errorMessage = err.message || 'Failed to send file'
      setError(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    } finally {
      setIsUploading(false)
    }
  }

  // Reset file input completely
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      // Force the input to be recreated by changing the key
      setFileInputKey(prev => prev + 1)
    }
  }

  // Clear selected file
  const clearFile = () => {
    setSelectedFile(null)
    setError('')
    resetFileInput()
    // Notify parent about file removal
    if (onFileSelected) {
      onFileSelected(null)
    }
  }

  // Toggle file input visibility
  const toggleFileInput = () => {
    setShowFileInput(!showFileInput)
    setError('')
    // Reset file input when toggling to ensure it can be used again
    resetFileInput()
  }

  // Direct file selection (for external calls)
  const selectFile = () => {
    resetFileInput()
    // Add a small delay to ensure the input is properly reset
    setTimeout(() => {
      fileInputRef.current?.click()
    }, 10)
  }

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handleSend,
    canSend: !!selectedFile && !isUploading,
    isUploading,
    selectedFile,
    clearFile,
    toggleFileInput,
    selectFile
  }), [selectedFile, isUploading, handleSend, toggleFileInput, selectFile])

  // Get file preview
  const getFilePreview = (file: File) => {
    const isImage = file.type.startsWith('image/')
    const icon = isImage ? ImageIcon : FileIcon
    const IconComponent = icon

    return (
      <div className="flex items-center space-x-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
        {isUploading ? (
          <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
        ) : (
          <IconComponent className="h-4 w-4 text-blue-600" />
        )}
        <span className="text-sm text-blue-800 truncate max-w-[200px]">
          {file.name}
        </span>
        <span className="text-xs text-blue-600">
          ({chatFileUploadService.formatFileSize(file.size)})
        </span>
        {isUploading && (
          <span className="text-xs text-blue-600 animate-pulse">
            Uploading...
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-blue-600 hover:text-blue-800"
          onClick={clearFile}
          disabled={isUploading}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Error display */}
      {error && (
        <Alert className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {/* File preview */}
      {selectedFile && (
        <div className="space-y-2">
          {getFilePreview(selectedFile)}
        </div>
      )}

      {/* File input (hidden) */}
      <Input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
        onChange={handleFileSelect}
        disabled={disabled || isUploading}
        key={`file-input-${fileInputKey}`}
      />

      {/* File selection area */}
      {showFileInput && !selectedFile && (
        <div 
          className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:border-gray-400 transition-colors"
          onClick={() => {
            resetFileInput()
            fileInputRef.current?.click()
          }}
        >
          <div className="flex flex-col items-center space-y-2">
            <Paperclip className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-600">
              Click to select a file
            </p>
            <p className="text-xs text-gray-500">
              Images, PDFs, and documents up to 10MB
            </p>
          </div>
        </div>
      )}

      {/* Action buttons - only show if not hiding them */}
      {!hideActionButtons && (
        <div className="flex items-center space-x-2">
          {!selectedFile && (
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFileInput}
              disabled={disabled || isUploading}
              className="flex-shrink-0"
            >
              <Paperclip className="h-4 w-4 mr-1" />
              {showFileInput ? 'Cancel' : 'Attach File'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
})

ChatFileUpload.displayName = 'ChatFileUpload'

export default ChatFileUpload